import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { useTenant } from "@/contexts/TenantContext";
import { Button } from "@/components/ui/button";
import SetAppointmentDialog from "@/components/appointments/SetAppointmentDialog";
import { Card, CardContent } from "@/components/ui/card";
import { usePageTitle } from "@/contexts/PageTitleContext";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { getBreadcrumbBackPath } from "@/lib/breadcrumbs";
import { buildPathWithCurrentTenant } from "@/lib/tenantPath";
import { 
  ArrowLeft, 
  CalendarPlus, 
  Pencil, 
  Play, 
  ShieldAlert, 
  ShieldCheck, 
  AlertTriangle,
  Clock,
  ExternalLink,
  RefreshCw,
  XCircle,
  CheckCircle,
} from "lucide-react";
import LeadAttributionPanel from "@/components/LeadAttributionPanel";
import LeadAttributionIntegrityCard from "@/components/attribution/LeadAttributionIntegrityCard";
import HouseholdDetailsCard from "@/components/HouseholdDetailsCard";
import { DetailPageSkeleton } from "@/components/skeletons/DetailPageSkeleton";


// =============================================================================
// MA-COMPLIANCE-002: TrustedForm Status Badge Component
// =============================================================================

/**
 * TrustedFormStatusBadge - Displays TrustedForm verification status
 * 
 * Badge states (MA-COMPLIANCE-002 CORRECTED):
 * - Mode OFF (gray): TrustedForm not used for this funnel
 * - Mode CAPTURE_ONLY (blue): Certificate captured for record-keeping, no enforcement
 * - Missing (red): No cert URL captured (enforce mode)
 * - Pending/Unverified (yellow): Verification pending or no API key available
 * - Compliant (green): Certificate retained and API verified
 * - Not Retained (orange/warning): API verified failure, quarantined
 */
function TrustedFormStatusBadge({ trustedform, isQuarantined, hasOverride }) {
  const status = trustedform?.status || 'legacy_unknown';
  const mode = trustedform?.mode || 'enforce';
  const verificationMode = trustedform?.verification_mode;
  
  // Determine badge display
  let badgeConfig = {
    color: 'bg-slate-500/20 text-slate-300 ring-slate-500/30',
    icon: AlertTriangle,
    label: 'Unknown',
    description: 'TrustedForm status not available',
  };
  
  if (hasOverride) {
    badgeConfig = {
      color: 'bg-amber-500/20 text-amber-300 ring-amber-500/30',
      icon: ShieldCheck,
      label: 'Manual Override',
      description: 'Approved manually for engagement',
    };
  } else if (mode === 'off') {
    // Mode OFF - no TrustedForm processing
    badgeConfig = {
      color: 'bg-slate-500/20 text-slate-400 ring-slate-500/30',
      icon: Clock,
      label: 'Off',
      description: 'TrustedForm not used for this funnel',
    };
  } else if (mode === 'capture_only') {
    // Mode CAPTURE_ONLY - captured but no enforcement
    badgeConfig = {
      color: 'bg-blue-500/20 text-blue-300 ring-blue-500/30',
      icon: CheckCircle,
      label: 'Captured',
      description: `Certificate captured for records (${status || 'no status'}). No compliance enforcement.`,
    };
  } else if (status === 'missing') {
    badgeConfig = {
      color: 'bg-red-500/20 text-red-300 ring-red-500/30',
      icon: XCircle,
      label: 'Missing',
      description: 'No TrustedForm certificate captured',
    };
  } else if (status === 'captured_pending_verification') {
    // Differentiate between active verification and unverified (no API key)
    if (verificationMode === 'unverified_autoretain_assumed') {
      badgeConfig = {
        color: 'bg-yellow-500/20 text-yellow-300 ring-yellow-500/30',
        icon: Clock,
        label: 'Unverified',
        description: 'Certificate captured but unverified (no API key). Assumes Auto-Retain.',
      };
    } else {
      badgeConfig = {
        color: 'bg-yellow-500/20 text-yellow-300 ring-yellow-500/30',
        icon: Clock,
        label: 'Verifying',
        description: 'Certificate verification in progress',
      };
    }
  } else if (status === 'retained') {
    badgeConfig = {
      color: 'bg-emerald-500/20 text-emerald-300 ring-emerald-500/30',
      icon: CheckCircle,
      label: 'Compliant',
      description: 'Certificate API verified and retained',
    };
  } else if (status === 'captured_not_retained') {
    badgeConfig = {
      color: 'bg-orange-500/20 text-orange-300 ring-orange-500/30',
      icon: AlertTriangle,
      label: 'Not Retained',
      description: 'API verified: certificate not retained - quarantined',
    };
  }
  
  const Icon = badgeConfig.icon;
  
  return (
    <div 
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ring-1 ${badgeConfig.color}`}
      title={badgeConfig.description}
      data-testid="trustedform-status-badge"
    >
      <Icon className="h-3.5 w-3.5" />
      <span>{badgeConfig.label}</span>
    </div>
  );
}


// =============================================================================
// MA-COMPLIANCE-002: TrustedForm Details Panel Component
// =============================================================================

/**
 * TrustedFormDetailsPanel - Shows detailed TrustedForm verification info
 * 
 * MA-COMPLIANCE-002 CORRECTED: Shows verification_mode and compliance_confidence
 */
function TrustedFormDetailsPanel({ leadId, trustedform, certUrl, onRefresh }) {
  const { toast } = useToast();
  const [verifying, setVerifying] = useState(false);
  
  const handleRetryVerification = async () => {
    setVerifying(true);
    try {
      await api.post(`/leads/${leadId}/trustedform/verify`);
      toast({
        title: "Verification scheduled",
        description: "TrustedForm verification has been triggered. Status will update shortly.",
      });
      onRefresh?.();
    } catch (err) {
      toast({
        title: "Verification failed",
        description: err?.response?.data?.detail || "Failed to trigger verification",
        variant: "destructive",
      });
    } finally {
      setVerifying(false);
    }
  };
  
  const status = trustedform?.status || 'unknown';
  const trustedformMode = trustedform?.mode || 'enforce';
  const attempts = trustedform?.verification_attempts || 0;
  const retainedAt = trustedform?.retained_at;
  const lastChecked = trustedform?.last_checked_at;
  const verificationError = trustedform?.verification_error;
  const verificationMode = trustedform?.verification_mode;
  const complianceConfidence = trustedform?.compliance_confidence;
  
  // Format TrustedForm mode for display
  const formatTrustedFormMode = (mode) => {
    switch (mode) {
      case 'off': return 'Off (No Processing)';
      case 'capture_only': return 'Capture Only (No Enforcement)';
      case 'enforce': return 'Enforce (Full Compliance)';
      default: return mode || 'Unknown';
    }
  };
  
  // Format verification mode for display
  const formatVerificationMode = (mode) => {
    switch (mode) {
      case 'api_verified': return 'API Verified';
      case 'unverified_autoretain_assumed': return 'Unverified (Assumes Auto-Retain)';
      case 'none': return 'None';
      default: return mode || 'Unknown';
    }
  };
  
  // Format compliance confidence for display
  const formatComplianceConfidence = (confidence) => {
    switch (confidence) {
      case 'high': return 'High (API Verified)';
      case 'assumed': return 'Assumed (No API Key)';
      case 'none': return 'None';
      default: return confidence || 'Unknown';
    }
  };
  
  return (
    <div className="mt-4 p-4 rounded-lg bg-slate-800/50 border border-white/10">
      <h4 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-indigo-400" />
        TrustedForm Details
      </h4>
      
      <div className="space-y-2 text-sm">
        {/* Mode */}
        <div className="flex items-center gap-2">
          <span className="text-slate-400 w-32 flex-shrink-0">Mode:</span>
          <span className={`text-slate-200 ${trustedformMode === 'off' ? 'text-slate-400' : trustedformMode === 'capture_only' ? 'text-blue-300' : 'text-emerald-300'}`}>
            {formatTrustedFormMode(trustedformMode)}
          </span>
        </div>
        
        {/* Certificate URL */}
        {certUrl && (
          <div className="flex items-start gap-2">
            <span className="text-slate-400 w-32 flex-shrink-0">Cert URL:</span>
            <a 
              href={certUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-400 hover:text-indigo-300 flex items-center gap-1 break-all"
              data-testid="trustedform-cert-link"
            >
              {certUrl.length > 50 ? certUrl.slice(0, 50) + '...' : certUrl}
              <ExternalLink className="h-3 w-3 flex-shrink-0" />
            </a>
          </div>
        )}
        
        {/* Status - only show if mode is not "off" */}
        {trustedformMode !== 'off' && (
          <div className="flex items-center gap-2">
            <span className="text-slate-400 w-32 flex-shrink-0">Status:</span>
            <span className="text-slate-200">
              {status === 'missing' && 'Missing'}
              {status === 'captured_pending_verification' && 'Pending Verification'}
              {status === 'retained' && 'Retained (Compliant)'}
              {status === 'captured_not_retained' && 'Not Retained (Failed)'}
              {(!status || status === 'unknown') && 'Unknown'}
            </span>
          </div>
        )}
        
        {/* Verification Mode */}
        {verificationMode && verificationMode !== 'none' && trustedformMode !== 'off' && (
          <div className="flex items-center gap-2">
            <span className="text-slate-400 w-32 flex-shrink-0">Verification:</span>
            <span className={`text-slate-200 ${verificationMode === 'api_verified' ? 'text-emerald-300' : 'text-yellow-300'}`}>
              {formatVerificationMode(verificationMode)}
            </span>
          </div>
        )}
        
        {/* Compliance Confidence */}
        {complianceConfidence && complianceConfidence !== 'none' && (
          <div className="flex items-center gap-2">
            <span className="text-slate-400 w-32 flex-shrink-0">Confidence:</span>
            <span className={`text-slate-200 ${complianceConfidence === 'high' ? 'text-emerald-300' : 'text-yellow-300'}`}>
              {formatComplianceConfidence(complianceConfidence)}
            </span>
          </div>
        )}
        
        {/* Verification Attempts */}
        {attempts > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-slate-400 w-32 flex-shrink-0">Attempts:</span>
            <span className="text-slate-200">{attempts} / 5</span>
          </div>
        )}
        
        {/* Retained At */}
        {retainedAt && (
          <div className="flex items-center gap-2">
            <span className="text-slate-400 w-32 flex-shrink-0">Retained At:</span>
            <span className="text-slate-200">
              {new Date(retainedAt).toLocaleString()}
            </span>
          </div>
        )}
        
        {/* Last Checked */}
        {lastChecked && (
          <div className="flex items-center gap-2">
            <span className="text-slate-400 w-32 flex-shrink-0">Last Check:</span>
            <span className="text-slate-200">
              {new Date(lastChecked).toLocaleString()}
            </span>
          </div>
        )}
        
        {/* Verification Error/Note */}
        {verificationError && (
          <div className="flex items-start gap-2 mt-2 p-2 rounded bg-amber-500/10 border border-amber-500/20">
            <AlertTriangle className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <span className="text-amber-300 text-xs">{verificationError}</span>
          </div>
        )}
        
        {/* Info message for unverified status */}
        {status === 'captured_pending_verification' && verificationMode === 'unverified_autoretain_assumed' && (
          <div className="flex items-start gap-2 mt-2 p-2 rounded bg-blue-500/10 border border-blue-500/20">
            <Clock className="h-4 w-4 text-blue-400 flex-shrink-0 mt-0.5" />
            <span className="text-blue-300 text-xs">
              Certificate URL valid. Cannot verify retention without TrustedForm API key. 
              Relies on Auto-Retain configuration in ActiveProspect. 
              Engagement is quarantined until API verification is available.
            </span>
          </div>
        )}
        
        {/* MA-COMPLIANCE-VISIBILITY-002: Action State Logic */}
        {/* Show ONLY ONE state based on mode and cert presence */}
        
        {/* State 1: Retry TrustedForm Verification - when cert exists but not retained */}
        {(trustedformMode === 'capture_only' || trustedformMode === 'enforce') && 
         certUrl && 
         status !== 'retained' && (
          <div className="pt-3 border-t border-white/10 mt-3">
            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-400">
                {trustedformMode === 'capture_only' 
                  ? 'Capture Only mode — No automatic claim' 
                  : 'Certificate not yet retained'}
              </div>
              {trustedformMode === 'enforce' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetryVerification}
                  disabled={verifying}
                  className="text-xs"
                  data-testid="retry-trustedform-verification"
                >
                  <RefreshCw className={`h-3 w-3 mr-1 ${verifying ? 'animate-spin' : ''}`} />
                  {verifying ? 'Claiming...' : 'Retry TrustedForm Claim'}
                </Button>
              )}
            </div>
          </div>
        )}
        
        {/* State 2: No Certificate Captured - when no cert URL */}
        {(trustedformMode === 'capture_only' || trustedformMode === 'enforce') && 
         !certUrl && (
          <div className="pt-3 border-t border-white/10 mt-3">
            <div className="flex items-center gap-2 text-amber-300 text-xs">
              <AlertTriangle className="h-4 w-4" />
              No TrustedForm certificate captured. Retry not available.
            </div>
          </div>
        )}
        
        {/* State 3: TrustedForm Retained - success state */}
        {status === 'retained' && (
          <div className="pt-3 border-t border-white/10 mt-3">
            <div className="flex items-center gap-2 text-emerald-300 text-xs">
              <CheckCircle className="h-4 w-4" />
              Certificate retained. Lead is compliant.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function formatMmddyyyy(value) {
  if (!value) return "—";
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[2]}/${m[3]}/${m[1]}`;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = String(d.getFullYear());
    return `${mm}/${dd}/${yyyy}`;
  }
  return s;
}

function formatErrorDetail(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return "Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => item?.msg || item?.message || JSON.stringify(item))
      .filter(Boolean)
      .join(" · ");
  }
  try {
    return JSON.stringify(detail);
  } catch {
    return String(detail);
  }
}

function hasValue(value) {
  if (value === null || value === undefined) return false;
  const s = String(value).trim();
  return s !== "" && s !== "—";
}

function EditLeadDialog({ lead, open, onOpenChange, onSaved }) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm({
      lead_source: lead?.lead_source || "",
      campaign_name: lead?.campaign_name || "",
      ad_set_name: lead?.ad_set_name || "",
      ad_name: lead?.ad_name || "",
      form_name: lead?.form_name || "",
      lead_external_id: lead?.lead_external_id || "",
      full_name: lead?.full_name || "",
      phone: lead?.phone || "",
      email: lead?.email || "",
      state: lead?.state || "",
      county: lead?.county || "",
      dob: lead?.dob || "",
      lead_type: lead?.lead_type || "",
      referral_agent_name: lead?.referral_agent_name || "",
      lead_create_date: lead?.lead_create_date || "",
      status: lead?.status || "New",
    });
  }, [open, lead]);

  if (!form) return null;

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.full_name.trim()) {
      toast({ title: "Missing name", description: "Full Name is required.", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        full_name: form.full_name.trim(),
      };
      Object.keys(payload).forEach((key) => {
        if (payload[key] === "") payload[key] = null;
      });
      const res = await api.put(`/leads/${lead.id}`, payload);
      toast({ title: "Saved" });
      onSaved?.(res.data);
      onOpenChange(false);
      
      // P1 HARDENING (A4): Removed legacy lead-data-updated custom event
      // React Query cache invalidation is now handled by the parent component's onSaved callback
    } catch (e) {
      toast({ title: "Could not save", description: formatErrorDetail(e), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-3xl">
        <DialogHeader>
          <DialogTitle>Edit lead</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-slate-200/80">Full Name *</Label>
            <Input value={form.full_name} onChange={onChange("full_name")} className="bg-white/5 ring-1 ring-white/10" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-200/80">Phone Number</Label>
            <Input value={form.phone} onChange={onChange("phone")} className="bg-white/5 ring-1 ring-white/10" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-200/80">Email</Label>
            <Input value={form.email} onChange={onChange("email")} className="bg-white/5 ring-1 ring-white/10" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-200/80">State</Label>
            <Input value={form.state} onChange={onChange("state")} className="bg-white/5 ring-1 ring-white/10" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-200/80">County</Label>
            <Input value={form.county} onChange={onChange("county")} className="bg-white/5 ring-1 ring-white/10" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-200/80">DOB</Label>
            <Input type="date" value={form.dob} onChange={onChange("dob")} className="bg-white/5 ring-1 ring-white/10" />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-200/80">Lead Source</Label>
            <Input value={form.lead_source_name || form.lead_source} onChange={onChange("lead_source")} className="bg-white/5 ring-1 ring-white/10" readOnly />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-200/80">Lead Create Date</Label>
            <Input type="date" value={form.lead_create_date} onChange={onChange("lead_create_date")} className="bg-white/5 ring-1 ring-white/10" />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-200/80">Campaign Name</Label>
            <Input value={form.campaign_name} onChange={onChange("campaign_name")} className="bg-white/5 ring-1 ring-white/10" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-200/80">Ad Set Name</Label>
            <Input value={form.ad_set_name} onChange={onChange("ad_set_name")} className="bg-white/5 ring-1 ring-white/10" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-200/80">Ad Name</Label>
            <Input value={form.ad_name} onChange={onChange("ad_name")} className="bg-white/5 ring-1 ring-white/10" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-200/80">Form Name</Label>
            <Input value={form.form_name} onChange={onChange("form_name")} className="bg-white/5 ring-1 ring-white/10" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-200/80">GHL Contact ID</Label>
            <Input value={form.lead_external_id} onChange={onChange("lead_external_id")} className="bg-white/5 ring-1 ring-white/10" />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-200/80">Vertical</Label>
            <Input value={form.lead_type} onChange={onChange("lead_type")} className="bg-white/5 ring-1 ring-white/10" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-200/80">Referral Agent Name</Label>
            <Input value={form.referral_agent_name} onChange={onChange("referral_agent_name")} className="bg-white/5 ring-1 ring-white/10" />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-200/80">Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
              <SelectTrigger className="bg-white/5 ring-1 ring-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="glass border-white/10">
                <SelectItem value="New">New</SelectItem>
                <SelectItem value="Booked">Booked</SelectItem>
                <SelectItem value="Converted">Converted</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            className="rounded-full bg-white/15 text-slate-100 ring-1 ring-white/20 hover:bg-white/20"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button className="rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500" onClick={submit} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StartRunDialog({ lead, open, onOpenChange }) {
  const { toast } = useToast();
  const navigate = useNavigate();

  const [busy, setBusy] = useState(false);
  const [productTypes, setProductTypes] = useState([]);
  const [scriptTypes, setScriptTypes] = useState([]);
  const [scripts, setScripts] = useState([]);

  const [productTypeId, setProductTypeId] = useState("");
  const [scriptTypeId, setScriptTypeId] = useState("");
  const [scriptId, setScriptId] = useState("");

  useEffect(() => {
    if (!open) return;

    const load = async () => {
      try {
        const pts = await api.get("/product-types");
        setProductTypes(pts.data || []);
      } catch (e) {
        toast({ title: "Could not load playbooks", variant: "destructive" });
      }
    };

    load();
    setProductTypeId("");
    setScriptTypeId("");
    setScriptId("");
    setScriptTypes([]);
    setScripts([]);
  }, [open]);

  useEffect(() => {
    if (!productTypeId) return;
    const load = async () => {
      try {
        const sts = await api.get(`/product-types/${productTypeId}/script-types`);
        setScriptTypes(sts.data || []);
      } catch {
        setScriptTypes([]);
      }
    };
    load();
    setScriptTypeId("");
    setScriptId("");
    setScripts([]);
  }, [productTypeId]);

  useEffect(() => {
    if (!productTypeId || !scriptTypeId) return;
    const load = async () => {
      try {
        const ss = await api.get(`/product-types/${productTypeId}/scripts`, { params: { script_type_id: scriptTypeId } });
        setScripts(ss.data || []);
      } catch {
        setScripts([]);
      }
    };
    load();
    setScriptId("");
  }, [productTypeId, scriptTypeId]);

  const onStart = async () => {
    if (!scriptId) return;
    setBusy(true);
    try {
      // NOTE: Runner now shows the Greeting Node first and creates the run only after the
      // user clicks a Greeting option.
      navigate(buildPathWithCurrentTenant(`/app/scripts/${scriptId}/play?lead_id=${encodeURIComponent(lead.id)}&p1=${encodeURIComponent(lead.full_name || "")}`));
      onOpenChange(false);
    } catch (e) {
      toast({ title: "Could not start run", description: formatErrorDetail(e), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-xl">
        <DialogHeader>
          <DialogTitle>Start Run</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-200/80">Playbook</Label>
            <Select value={productTypeId} onValueChange={setProductTypeId}>
              <SelectTrigger className="bg-white/5 ring-1 ring-white/10">
                <SelectValue placeholder="Select playbook" />
              </SelectTrigger>
              <SelectContent className="glass border-white/10">
                {productTypes.map((pt) => (
                  <SelectItem key={pt.id} value={pt.id}>
                    {pt.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-200/80">Script Type</Label>
            <Select value={scriptTypeId} onValueChange={setScriptTypeId} disabled={!productTypeId}>
              <SelectTrigger className="bg-white/5 ring-1 ring-white/10">
                <SelectValue placeholder={productTypeId ? "Select script type" : "Select playbook first"} />
              </SelectTrigger>
              <SelectContent className="glass border-white/10">
                {scriptTypes.map((st) => (
                  <SelectItem key={st.id} value={st.id}>
                    {st.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-200/80">Script</Label>
            <Select value={scriptId} onValueChange={setScriptId} disabled={!scriptTypeId}>
              <SelectTrigger className="bg-white/5 ring-1 ring-white/10">
                <SelectValue placeholder={scriptTypeId ? "Select script" : "Select script type first"} />
              </SelectTrigger>
              <SelectContent className="glass border-white/10">
                {scripts.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            className="rounded-full bg-white/15 text-slate-100 ring-1 ring-white/20 hover:bg-white/20"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button className="rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500" onClick={onStart} disabled={busy || !scriptId}>
            {busy ? "Starting…" : "Start"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * ApproveEngagementButton - Admin action to manually approve a quarantined lead for engagement
 * MA-QUARANTINE-RELEASE-001: Shows dialog, triggers quarantine release + original outbound dispatch
 * 
 * Props:
 *   leadId: string - The lead ID
 *   outboundDeliveryStatus: string - Current delivery status (to show already dispatched state)
 *   onApproved: function - Callback after approval
 */
function ApproveEngagementButton({ leadId, outboundDeliveryStatus, onApproved }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("TrustedForm manually verified");
  const [trustedformUrl, setTrustedformUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  
  // MA-QUARANTINE-RELEASE-001 REFINEMENT: Show already dispatched state
  const isAlreadyDispatched = outboundDeliveryStatus === "sent";

  const handleApprove = async () => {
    if (!notes.trim() || notes.trim().length < 5) {
      toast({
        title: "Note required",
        description: "Please provide a note explaining the manual verification (at least 5 characters).",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        notes: notes.trim(),
      };
      
      // Include trustedform_cert_url if provided
      if (trustedformUrl.trim()) {
        payload.trustedform_cert_url = trustedformUrl.trim();
      }

      const response = await api.post(`/admin/lead-compliance/leads/${leadId}/approve-engagement`, payload);
      
      // MA-QUARANTINE-RELEASE-001: Check dispatch result and re-quarantine status
      const dispatchResult = response.data?.outbound_dispatch || response.data?.ghl_callback?.outbound_dispatch;
      const dispatchStatus = dispatchResult?.status;
      const reQuarantined = response.data?.re_quarantined;
      const reQuarantineReason = response.data?.re_quarantine_reason;

      if (reQuarantined) {
        // Lead was re-quarantined due to dispatch failure
        if (reQuarantineReason === "dispatch_failed_no_destination") {
          toast({
            title: "Dispatch failed - No destination",
            description: "Lead was released but re-quarantined: no outbound destination is configured. Please configure a destination first.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Dispatch failed",
            description: "Lead was released but re-quarantined due to dispatch failure. Check diagnostics for details.",
            variant: "destructive",
          });
        }
      } else if (dispatchStatus === "sent") {
        // Success: dispatched
        toast({
          title: "Lead approved and dispatched",
          description: "The lead has been released from quarantine and sent to the outbound destination.",
        });
      } else if (dispatchStatus === "already_delivered") {
        // Already sent
        toast({
          title: "Lead approved (already dispatched)",
          description: "The lead was released from quarantine. It was already dispatched previously.",
        });
      } else {
        // Other status
        toast({
          title: "Lead approved",
          description: response.data?.message || "The lead has been approved for engagement.",
        });
      }

      setOpen(false);
      setTrustedformUrl(""); // Reset for next use
      onApproved?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to approve lead";
      toast({
        title: "Approval failed",
        description: detail,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  // MA-QUARANTINE-RELEASE-001 REFINEMENT: Show already dispatched state instead of button
  if (isAlreadyDispatched) {
    return (
      <div className="flex items-center gap-2 text-sm text-emerald-300">
        <CheckCircle className="h-4 w-4" />
        <span>Already dispatched</span>
      </div>
    );
  }

  return (
    <>
      <Button
        variant="secondary"
        className="rounded-full bg-amber-500/20 text-amber-100 ring-1 ring-amber-400/30 hover:bg-amber-500/30"
        onClick={() => setOpen(true)}
        data-testid="approve-engagement-button"
      >
        <ShieldCheck className="mr-2 h-4 w-4" />
        Approve for Engagement
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass sm:max-w-md" data-testid="approve-engagement-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-100 flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-amber-400" />
              Approve Lead for Engagement
            </DialogTitle>
            <DialogDescription className="text-slate-300">
              You are manually approving this lead for engagement. This action will:
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <ul className="text-sm text-slate-300 space-y-1 ml-4 list-disc">
              <li>Remove the compliance quarantine</li>
              <li>Set <code className="text-xs bg-white/10 px-1 rounded">ma_call_text_eligible = true</code></li>
              <li>Trigger outbound dispatch to original destination</li>
              <li>Log this action for audit purposes</li>
            </ul>

            <div className="space-y-2">
              <Label htmlFor="trustedform-url" className="text-slate-200">
                TrustedForm Certificate URL <span className="text-slate-400">(optional)</span>
              </Label>
              <Input
                id="trustedform-url"
                type="url"
                placeholder="https://cert.trustedform.com/..."
                value={trustedformUrl}
                onChange={(e) => setTrustedformUrl(e.target.value)}
                className="bg-white/5 text-slate-100 ring-1 ring-white/10"
                data-testid="trustedform-url-input"
              />
              <p className="text-xs text-slate-400">
                If you have the TrustedForm certificate URL, enter it here to store on the lead record.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="approval-notes" className="text-slate-200">
                Verification Note <span className="text-amber-400">*</span>
              </Label>
              <Textarea
                id="approval-notes"
                placeholder="e.g., TrustedForm manually verified via ActiveProspect dashboard"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="bg-white/5 text-slate-100 ring-1 ring-white/10 min-h-[80px]"
                data-testid="approval-notes-input"
              />
              <p className="text-xs text-slate-400">
                This note will be logged for audit purposes along with who approved and when.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="secondary"
              className="rounded-full bg-white/15 text-slate-100 ring-1 ring-white/20 hover:bg-white/20"
              onClick={() => setOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              className="rounded-full bg-amber-500/90 text-white hover:bg-amber-500"
              onClick={handleApprove}
              disabled={submitting || notes.trim().length < 5}
              data-testid="confirm-approval-button"
            >
              {submitting ? "Releasing…" : "Confirm & Dispatch"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function LeadDetailsPage() {
  const { toast } = useToast();
  const { leadId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();

  // P1 HARDENING (A3): Invalidate both list AND detail queries after mutations
  const invalidateLeadsCache = useCallback(() => {
    // Invalidate the list queries
    queryClient.invalidateQueries({
      queryKey: queryKeys.leads.all(selectedTenantId),
    });
    // Invalidate the specific detail query for this lead
    if (leadId) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.leads.detail(selectedTenantId, leadId),
      });
    }
  }, [queryClient, selectedTenantId, leadId]);

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientCreateDate, setClientCreateDate] = useState(null);
  const [firstAppointmentCreatedAt, setFirstAppointmentCreatedAt] = useState(null);
  const [householdData, setHouseholdData] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [startRunOpen, setStartRunOpen] = useState(false);
  const [apptOpen, setApptOpen] = useState(false);

  usePageTitle(lead?.full_name || "Lead Details");

  const prospectFields = useMemo(
    () => [
      { key: "phone", label: "Phone", value: lead?.phone, display: lead?.phone || "" },
      { key: "email", label: "Email", value: lead?.email, display: lead?.email || "" },
      { key: "street_address", label: "Street Address", value: lead?.address, display: lead?.address || "" },
      { key: "city", label: "City", value: lead?.city, display: lead?.city || "" },
      { key: "state", label: "State", value: lead?.state, display: lead?.state || "" },
      { key: "zip", label: "Zip Code", value: lead?.zip, display: lead?.zip || "" },
      { key: "county", label: "County", value: lead?.county, display: lead?.county || "" },
      { key: "dob", label: "DOB", value: lead?.dob, display: formatMmddyyyy(lead?.dob) },
    ],
    [lead]
  );

  const visibleProspectFields = useMemo(
    () => prospectFields.filter((field) => hasValue(field.value)),
    [prospectFields]
  );

  const leadDetailFields = useMemo(
    () => [
      { key: "lead_source", label: "Lead Source", value: lead?.lead_source_name || lead?.lead_source, display: lead?.lead_source_name || lead?.lead_source || "" },
      { key: "lead_type", label: "Vertical", value: lead?.lead_type, display: lead?.lead_type || "" },
      { key: "lead_create_date", label: "Lead Create Date", value: lead?.lead_create_date, display: formatMmddyyyy(lead?.lead_create_date) },
      {
        key: "client_create_date",
        label: "Client Create Date",
        value: clientCreateDate,
        display: formatMmddyyyy(clientCreateDate),
      },
      {
        key: "appointment_create_date",
        label: "Appointment Create Date",
        value: firstAppointmentCreatedAt,
        display: formatMmddyyyy(firstAppointmentCreatedAt),
      },
      { key: "campaign_name", label: "Campaign Name", value: lead?.campaign_name, display: lead?.campaign_name || "" },
      { key: "ad_set_name", label: "Ad Set Name", value: lead?.ad_set_name, display: lead?.ad_set_name || "" },
      { key: "ad_name", label: "Ad Name", value: lead?.ad_name, display: lead?.ad_name || "" },
      { key: "form_name", label: "Form Name", value: lead?.form_name, display: lead?.form_name || "" },
      { key: "ghl_contact_id", label: "GHL Contact ID", value: lead?.ghl_contact_id || lead?.lead_external_id, display: lead?.ghl_contact_id || lead?.lead_external_id || "", valueClassName: "break-all" },
      { key: "missionassist_id", label: "MissionAssist Lead ID", value: lead?.id, display: lead?.id || "", valueClassName: "break-all" },
      { key: "household_id", label: "Household ID", value: lead?.household_id, display: lead?.household_id || "", valueClassName: "break-all" },
      { key: "referral_agent_name", label: "Referral Agent Name", value: lead?.referral_agent_name, display: lead?.referral_agent_name || "" },
    ],
    [lead, clientCreateDate, firstAppointmentCreatedAt]
  );

  const visibleLeadDetailFields = useMemo(
    () => leadDetailFields.filter((field) => hasValue(field.value)),
    [leadDetailFields]
  );

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/leads/${leadId}`);
      setLead(res.data);
    } catch (e) {
      toast({ title: "Lead not found", description: e?.response?.data?.detail || "", variant: "destructive" });
      navigate(buildPathWithCurrentTenant("/app/leads"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [leadId]);

  useEffect(() => {
    if (!leadId) {
      setClientCreateDate(null);
      setFirstAppointmentCreatedAt(null);
      setHouseholdData(null);
      return;
    }
    const loadRelated = async () => {
      try {
        const res = await api.get(`/clients/by-lead/${leadId}`);
        setClientCreateDate(res.data?.created_at || null);
      } catch {
        setClientCreateDate(null);
      }

      try {
        const apptRes = await api.get("/appointments", {
          params: {
            filters: JSON.stringify([{ field: "lead_id", operator: "equals", value: leadId }]),
            sort_by: "created_at",
            sort_dir: "asc",
            limit: 1,
            offset: 0,
          },
        });
        const first = apptRes.data?.items?.[0];
        setFirstAppointmentCreatedAt(first?.created_at || null);
      } catch {
        setFirstAppointmentCreatedAt(null);
      }

      // Fetch household members
      try {
        const householdRes = await api.get(`/leads/${leadId}/household-members`);
        setHouseholdData(householdRes.data || null);
      } catch {
        setHouseholdData(null);
      }
    };
    loadRelated();
  }, [leadId]);

  if (loading || !lead) {
    return (
      <div data-testid="lead-details-loading">
        <DetailPageSkeleton variant="default" />
      </div>
    );
  }

  // MA-COMPLIANCE-002: Enhanced compliance state checks
  const trustedform = lead.trustedform || {};
  const trustedformStatus = trustedform.status || (lead.trustedform_cert_url ? 'legacy_unknown' : 'missing');
  const certUrl = trustedform.cert_url || lead.trustedform_cert_url;
  
  // Determine quarantine state based on new trustedform system
  // MA-QUARANTINE-RELEASE-001: Support all TrustedForm-related quarantine reasons
  const isQuarantined = lead.is_quarantined;
  const trustedformQuarantineReasons = [
    "missing_trustedform_cert",
    "trustedform_pending_verification",
    "trustedform_not_retained",
    "trustedform_not_compliant",
    "trustedform_not_verified",  // Legacy reason
  ];
  const isQuarantinedForTrustedForm = isQuarantined && (
    trustedformQuarantineReasons.includes(lead.quarantine_reason) ||
    trustedformStatus === 'missing' ||
    trustedformStatus === 'captured_not_retained'
  );
  const isPendingVerification = trustedformStatus === 'captured_pending_verification';
  const isCompliant = trustedformStatus === 'retained';
  const hasManualOverride = lead.compliance_override || lead.compliance_status === "manual_trustedform_override";
  
  const engagementSyncStatus = lead.engagement_sync_status;
  const isEngagementPending = hasManualOverride && engagementSyncStatus === "pending";
  const isEngagementConfirmed = hasManualOverride && engagementSyncStatus === "confirmed";

  return (
    <div className="space-y-6" data-testid="lead-details-page">
      {/* MA-COMPLIANCE-002: TrustedForm Status Banner */}
      {/* Missing Certificate - Quarantined */}
      {(trustedformStatus === 'missing') && !hasManualOverride && (
        <div 
          className="rounded-xl bg-red-500/10 border border-red-400/20 p-4"
          data-testid="lead-trustedform-missing-banner"
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <ShieldAlert className="h-5 w-5 text-red-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-semibold text-red-200">
                  Missing TrustedForm Certificate
                </h3>
                <TrustedFormStatusBadge 
                  trustedform={trustedform} 
                  isQuarantined={isQuarantined}
                  hasOverride={hasManualOverride}
                />
              </div>
              <p className="mt-1 text-sm text-red-200/70">
                This lead arrived without a TrustedForm certificate URL. Lead is quarantined until compliance is verified.
              </p>
              <div className="mt-3">
                <ApproveEngagementButton 
                  leadId={lead.id}
                  outboundDeliveryStatus={lead.outbound_delivery_status}
                  onApproved={() => {
                    load();
                    invalidateLeadsCache();
                  }} 
                />
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Pending Verification - MA-QUARANTINE-RELEASE-001: Add approve button */}
      {isPendingVerification && !hasManualOverride && (
        <div 
          className="rounded-xl bg-yellow-500/10 border border-yellow-400/20 p-4"
          data-testid="lead-trustedform-verifying-banner"
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <Clock className="h-5 w-5 text-yellow-400 animate-pulse" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-semibold text-yellow-200">
                  TrustedForm Verification Pending
                </h3>
                <TrustedFormStatusBadge 
                  trustedform={trustedform} 
                  isQuarantined={isQuarantined}
                  hasOverride={hasManualOverride}
                />
              </div>
              <p className="mt-1 text-sm text-yellow-200/70">
                Certificate captured. Waiting for claim/retention verification. 
                {isQuarantined && " Lead is quarantined until compliance is verified."}
              </p>
              <TrustedFormDetailsPanel 
                leadId={lead.id}
                trustedform={trustedform}
                certUrl={certUrl}
                onRefresh={load}
              />
              {/* MA-QUARANTINE-RELEASE-001: Show approve button when quarantined */}
              {isQuarantined && (
                <div className="mt-3">
                  <ApproveEngagementButton 
                    leadId={lead.id}
                    outboundDeliveryStatus={lead.outbound_delivery_status}
                    onApproved={() => {
                      load();
                      invalidateLeadsCache();
                    }} 
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Not Retained - Quarantined */}
      {trustedformStatus === 'captured_not_retained' && !hasManualOverride && (
        <div 
          className="rounded-xl bg-orange-500/10 border border-orange-400/20 p-4"
          data-testid="lead-trustedform-not-retained-banner"
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <AlertTriangle className="h-5 w-5 text-orange-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-semibold text-orange-200">
                  TrustedForm Certificate Not Retained
                </h3>
                <TrustedFormStatusBadge 
                  trustedform={trustedform} 
                  isQuarantined={isQuarantined}
                  hasOverride={hasManualOverride}
                />
              </div>
              <p className="mt-1 text-sm text-orange-200/70">
                Certificate was captured but not retained in ActiveProspect after multiple verification attempts. 
                Please check your Auto-Retain configuration.
              </p>
              <TrustedFormDetailsPanel 
                leadId={lead.id}
                trustedform={trustedform}
                certUrl={certUrl}
                onRefresh={load}
              />
              <div className="mt-3">
                <ApproveEngagementButton 
                  leadId={lead.id}
                  outboundDeliveryStatus={lead.outbound_delivery_status}
                  onApproved={() => {
                    load();
                    invalidateLeadsCache();
                  }} 
                />
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Compliant */}
      {isCompliant && !hasManualOverride && (
        <div 
          className="rounded-xl bg-emerald-500/10 border border-emerald-400/20 p-4"
          data-testid="lead-trustedform-compliant-banner"
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <ShieldCheck className="h-5 w-5 text-emerald-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-semibold text-emerald-200">
                  TrustedForm Compliant
                </h3>
                <TrustedFormStatusBadge 
                  trustedform={trustedform} 
                  isQuarantined={isQuarantined}
                  hasOverride={hasManualOverride}
                />
              </div>
              <p className="mt-1 text-sm text-emerald-200/70">
                Certificate verified and retained. Lead is eligible for AI engagement.
              </p>
              <TrustedFormDetailsPanel 
                leadId={lead.id}
                trustedform={trustedform}
                certUrl={certUrl}
                onRefresh={load}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Manual Override Active */}
      {hasManualOverride && (
        <div 
          className="rounded-xl bg-amber-500/10 border border-amber-400/20 p-4"
          data-testid="lead-trustedform-override-banner"
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <ShieldCheck className="h-5 w-5 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-semibold text-amber-200">
                  Manual Compliance Override Active
                </h3>
                <TrustedFormStatusBadge 
                  trustedform={trustedform} 
                  isQuarantined={isQuarantined}
                  hasOverride={hasManualOverride}
                />
              </div>
              <p className="mt-1 text-sm text-amber-200/70">
                This lead has been manually approved for engagement. TrustedForm verification was bypassed.
                {lead.quarantine_resolution_notes && (
                  <span className="block mt-1 italic">Note: "{lead.quarantine_resolution_notes}"</span>
                )}
              </p>
              <TrustedFormDetailsPanel 
                leadId={lead.id}
                trustedform={trustedform}
                certUrl={certUrl}
                onRefresh={load}
              />
            </div>
          </div>
        </div>
      )}

      {/* Legacy: Engagement Pending Sync */}
      {/* Override Approved — Pending GHL Confirmation Banner */}
      {isEngagementPending && !isQuarantinedForTrustedForm && (
        <div 
          className="rounded-xl bg-blue-500/10 border border-blue-400/20 p-4"
          data-testid="lead-engagement-pending-banner"
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <AlertTriangle className="h-5 w-5 text-blue-400 animate-pulse" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-blue-200">
                Override Approved — Pending GHL Confirmation
              </h3>
              <p className="mt-1 text-sm text-blue-200/70">
                This lead was manually approved for engagement. Waiting for GHL to confirm that engagement tags have been applied.
                {lead.quarantine_resolution_notes && (
                  <span className="block mt-1 italic">Note: "{lead.quarantine_resolution_notes}"</span>
                )}
              </p>
              {lead.engagement_release_requested_at && (
                <p className="mt-1 text-xs text-blue-300/50">
                  Requested: {new Date(lead.engagement_release_requested_at).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Engagement Confirmed Banner */}
      {isEngagementConfirmed && !isQuarantinedForTrustedForm && (
        <div 
          className="rounded-xl bg-emerald-500/10 border border-emerald-400/20 p-4"
          data-testid="lead-engagement-confirmed-banner"
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <ShieldCheck className="h-5 w-5 text-emerald-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-emerald-200">
                Engagement Confirmed
              </h3>
              <p className="mt-1 text-sm text-emerald-200/70">
                GHL has confirmed that engagement tags were successfully applied. This lead is now fully released for engagement.
                {lead.quarantine_resolution_notes && (
                  <span className="block mt-1 italic">Note: "{lead.quarantine_resolution_notes}"</span>
                )}
              </p>
              {lead.engagement_release_confirmed_at && (
                <p className="mt-1 text-xs text-emerald-300/50">
                  Confirmed: {new Date(lead.engagement_release_confirmed_at).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Manual Override without sync tracking (legacy/fallback) */}
      {hasManualOverride && !engagementSyncStatus && !isQuarantinedForTrustedForm && (
        <div 
          className="rounded-xl bg-emerald-500/10 border border-emerald-400/20 p-4"
          data-testid="lead-compliance-override-banner"
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <ShieldCheck className="h-5 w-5 text-emerald-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-emerald-200">
                TrustedForm Manually Verified
              </h3>
              <p className="mt-1 text-sm text-emerald-200/70">
                This lead was manually approved for engagement after TrustedForm verification.
                {lead.quarantine_resolution_notes && (
                  <span className="block mt-1 italic">Note: "{lead.quarantine_resolution_notes}"</span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <Button
          className="rounded-full bg-indigo-500/15 text-indigo-100 ring-1 ring-indigo-400/20 hover:bg-indigo-500/25"
          onClick={() => navigate(getBreadcrumbBackPath("/app/leads"))}
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>

        <div className="flex flex-wrap items-center gap-2">
          <Button className="rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500" onClick={() => setStartRunOpen(true)}>
            <Play className="mr-2 h-4 w-4" /> Start Run
          </Button>
          {lead.status !== "Booked" && (
            <Button
              className="rounded-full bg-indigo-500/20 text-indigo-100 ring-1 ring-indigo-400/20 hover:bg-indigo-500/30"
              onClick={() => setApptOpen(true)}
              data-testid="lead-details-set-appointment-button"
            >
              <CalendarPlus className="mr-2 h-4 w-4" /> Set Appointment
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <Card className="glass" data-testid="lead-customer-card">
          <CardContent className="p-0">
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="text-lg font-semibold text-slate-100" data-testid="lead-details-prospect-title">
                Prospect Details
              </div>
              <Button
                variant="secondary"
                className="h-9 w-9 rounded-full bg-indigo-500/20 p-0 text-indigo-100 ring-1 ring-indigo-400/20 hover:bg-indigo-500/30"
                onClick={() => setEditOpen(true)}
                data-testid="lead-details-prospect-edit"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </div>
            <div className="border-t border-white/10 px-4 py-4">
              <div className="space-y-5 text-sm" data-testid="lead-details-prospect-fields">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {visibleProspectFields.map((field) => (
                    <div key={field.key}>
                      <div className="text-xs text-slate-200/70" data-testid={`lead-prospect-label-${field.key}`}>
                        {field.label}
                      </div>
                      <div className="text-slate-100" data-testid={`lead-prospect-value-${field.key}`}>
                        {field.display}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Household Details Card - placed between Prospect Details and Lead Details */}
        <HouseholdDetailsCard 
          householdData={householdData} 
          currentLeadId={leadId}
          linkPrefix="/app/leads"
        />

        <Card className="glass" data-testid="lead-details-card">
          <CardContent className="p-0">
            <div className="px-4 py-3">
              <div className="text-lg font-semibold text-slate-100" data-testid="lead-details-title">Lead Details</div>
            </div>
            <div className="border-t border-white/10 px-4 py-4">
              <div className="space-y-5 text-sm" data-testid="lead-details-fields">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {visibleLeadDetailFields.map((field) => (
                    <div key={field.key}>
                      <div className="text-xs text-slate-200/70" data-testid={`lead-details-label-${field.key}`}>
                        {field.label}
                      </div>
                      <div
                        className={`text-slate-100 ${field.valueClassName || ""}`}
                        data-testid={
                          field.key === "missionassist_id"
                            ? "lead-details-missionassist-id"
                            : `lead-details-value-${field.key}`
                        }
                      >
                        {field.display}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* MA-UI-110: Data Enrichment Card - "What MissionAssist knows about this lead" */}
        {(lead?.county || lead?.state || lead?.timezone_label || lead?.estimated_income_range || lead?.estimated_home_value_range) && (
          <Card className="glass" data-testid="lead-data-enrichment-card">
            <CardContent className="p-0">
              <div className="px-4 py-3">
                <div className="text-lg font-semibold text-slate-100" data-testid="lead-data-enrichment-title">
                  Data Enrichment
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  Insights derived from lead data
                </div>
              </div>
              <div className="border-t border-white/10 px-4 py-4">
                <div className="space-y-4 text-sm" data-testid="lead-data-enrichment-fields">
                  {/* Location Section */}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {lead?.state && (
                      <div data-testid="lead-enrichment-field-state">
                        <div className="text-xs text-slate-200/60 mb-1">
                          State
                        </div>
                        <div className="text-slate-100 font-medium" data-testid="lead-enrichment-value-state">
                          {lead.state}
                        </div>
                      </div>
                    )}
                    {lead?.county && (
                      <div data-testid="lead-enrichment-field-county">
                        <div className="text-xs text-slate-200/60 mb-1">
                          County
                        </div>
                        <div className="text-slate-100 font-medium" data-testid="lead-enrichment-value-county">
                          {lead.county}
                        </div>
                      </div>
                    )}
                    {lead?.timezone_label && (
                      <div data-testid="lead-enrichment-field-timezone">
                        <div className="text-xs text-slate-200/60 mb-1">
                          Time Zone
                        </div>
                        <div className="text-slate-100 font-medium" data-testid="lead-enrichment-value-timezone">
                          {lead.timezone_label}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Financial Section - MA-BE-078 */}
                  {(lead?.estimated_income_range || lead?.estimated_home_value_range) && (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 pt-3 border-t border-white/5">
                      {lead?.estimated_income_range && (
                        <div data-testid="lead-enrichment-field-income">
                          <div className="text-xs text-slate-200/60 mb-1">
                            Estimated Income
                          </div>
                          <div className="text-slate-100 font-medium" data-testid="lead-enrichment-value-income">
                            {lead.estimated_income_range}
                          </div>
                        </div>
                      )}
                      {lead?.estimated_home_value_range && (
                        <div data-testid="lead-enrichment-field-home-value">
                          <div className="text-xs text-slate-200/60 mb-1">
                            Estimated Home Value
                          </div>
                          <div className="text-slate-100 font-medium" data-testid="lead-enrichment-value-home-value">
                            {lead.estimated_home_value_range}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* MA-BE-076: AI Intelligence Card */}
        {lead?.ai_context_summary && (
          <Card className="glass" data-testid="lead-ai-intelligence-card">
            <CardContent className="p-0">
              <div className="px-4 py-3">
                <div className="text-lg font-semibold text-slate-100" data-testid="lead-ai-intelligence-title">
                  AI Intelligence
                </div>
              </div>
              <div className="border-t border-white/10 px-4 py-4">
                <div className="space-y-3 text-sm" data-testid="lead-ai-intelligence-fields">
                  <div>
                    <div className="text-xs text-slate-200/70 mb-1" data-testid="lead-ai-label-context-summary">
                      AI Context Summary
                    </div>
                    <div 
                      className="text-slate-100 bg-indigo-500/10 rounded-lg px-3 py-2 ring-1 ring-indigo-400/20"
                      data-testid="lead-ai-value-context-summary"
                    >
                      {lead.ai_context_summary}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* MA-ARCH-181: Attribution Integrity Card — source, confidence, lineage */}
        <LeadAttributionIntegrityCard lead={lead} />

        {/* Attribution Panel - shows touch history summary */}
        <LeadAttributionPanel leadId={leadId} />
      </div>

      <EditLeadDialog
        lead={lead}
        open={editOpen}
        onOpenChange={setEditOpen}
        onSaved={(next) => {
          setLead(next);
          // REACT QUERY: Invalidate leads list cache
          invalidateLeadsCache();
        }}
      />
      <StartRunDialog lead={lead} open={startRunOpen} onOpenChange={setStartRunOpen} />
      <SetAppointmentDialog
        lead={lead}
        open={apptOpen}
        onOpenChange={setApptOpen}
        onConverted={() => {
          load();
          // REACT QUERY: Invalidate leads and appointments caches
          invalidateLeadsCache();
          queryClient.invalidateQueries({
            queryKey: queryKeys.appointments.all(selectedTenantId),
          });
        }}
      />
    </div>
  );
}
